import "./FMRatedAge.scss";

import { ControlledInput } from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { MultiColumnCard } from "components/Card/MultiColumnCard";
import { KaboodleToaster } from "components/common";
import { MaskedInput } from "components/inputs";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  useGetRatedAgesQuery,
  useCreateRatedAgeMutation,
  useDeleteRatedAgeMutation,
} from "services/rated-age-api";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  FMServiceType,
  MSADataType,
  RatedAgeType,
} from "types/DataTypes";
import { SERVICE_TYPE } from "types/DataTypes";

import RatedAgeOCRStatus from "./RatedAgeOCRStatus";
import RatedAgeRow from "./RatedAgeRow";
import { validateAgeField } from "./rated-age-validation";

type RatedAgeFormValues = {
  claimant_chronological_age: number | null;
  median_rated_age: number | null;
  active_rated_age: number | null;
  rated_age_override: number | null;
  claimant_life_expectancy: number | null;
  msa_period?: number | null;
};

type RatedAgeFormProps = {
  serviceType?: FMServiceType;
  service: MSADataType | CostProjectionDataType | AmendedReviewScreeningType;
  onSave: (field: string | null, value: unknown) => void;
};

const hasRatedAgeFields = (
  service: MSADataType | CostProjectionDataType | AmendedReviewScreeningType,
): service is MSADataType | CostProjectionDataType => {
  return "claimant_chronological_age" in service;
};

const parseNumber = (value: string | number | null): number | null => {
  if (value === null) return null;
  const num = typeof value === "string" ? parseInt(value, 10) : value;
  return isNaN(num) ? null : num;
};

const parseDecimalNumber = (value: string | number | null): number | null => {
  if (value === null) return null;
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? null : num;
};

const RatedAgeForm = ({ serviceType, service, onSave }: RatedAgeFormProps) => {
  const [createRatedAge] = useCreateRatedAgeMutation();
  const [deleteRatedAge] = useDeleteRatedAgeMutation();

  // determine if this is MSA or CostProjection
  const isMSA =
    "type" in service &&
    (service.type === SERVICE_TYPE.MSA ||
      service.type === SERVICE_TYPE.MSA_111 ||
      service.type === SERVICE_TYPE.AMENDED_REVIEW ||
      service.type === SERVICE_TYPE.NTMSA ||
      service.type === SERVICE_TYPE.PRE_MSA);
  const msaId = isMSA && hasRatedAgeFields(service) ? service.id : undefined;
  const costProjectionId =
    !isMSA && hasRatedAgeFields(service) ? service.id : undefined;

  // fetch rated ages - only if service has an ID and rated age fields
  const shouldFetchRatedAges =
    hasRatedAgeFields(service) && service.id && (msaId || costProjectionId);

  const { data: ratedAgesData, isLoading: isLoadingRatedAges } =
    useGetRatedAgesQuery(
      {
        msaId,
        costProjectionId,
      },
      {
        skip: !shouldFetchRatedAges,
      },
    );

  // ensure ratedAges is always an array and sort by id to maintain stable order
  // sorting by id ascending ensures entries don't reorder when updated
  const ratedAges = Array.isArray(ratedAgesData)
    ? [...ratedAgesData].sort((a, b) => a.id - b.id)
    : [];

  // get the most recent median/active rated age from the rated ages data
  // the backend returns these computed values on each rated age entry
  const computedMedianRatedAge =
    ratedAges.length > 0 && ratedAges[0].median_rated_age !== null
      ? ratedAges[0].median_rated_age
      : hasRatedAgeFields(service)
        ? service.median_rated_age
        : null;

  const computedActiveRatedAge =
    ratedAges.length > 0 && ratedAges[0].active_rated_age !== null
      ? ratedAges[0].active_rated_age
      : hasRatedAgeFields(service)
        ? service.active_rated_age
        : null;

  const { control, reset } = useForm<RatedAgeFormValues>({
    defaultValues: {
      claimant_chronological_age: hasRatedAgeFields(service)
        ? parseNumber(service.claimant_chronological_age)
        : null,
      median_rated_age: parseNumber(computedMedianRatedAge),
      active_rated_age: parseNumber(computedActiveRatedAge),
      rated_age_override: hasRatedAgeFields(service)
        ? parseNumber(service.rated_age_override)
        : null,
      claimant_life_expectancy: hasRatedAgeFields(service)
        ? parseDecimalNumber(service.claimant_life_expectancy)
        : null,
      msa_period:
        hasRatedAgeFields(service) && "msa_period" in service
          ? parseNumber(service.msa_period)
          : null,
    },
  });

  useEffect(() => {
    if (!hasRatedAgeFields(service)) return;

    reset(
      {
        claimant_chronological_age: parseNumber(
          service.claimant_chronological_age,
        ),
        median_rated_age: parseNumber(computedMedianRatedAge),
        active_rated_age: parseNumber(computedActiveRatedAge),
        rated_age_override: parseNumber(service.rated_age_override),
        claimant_life_expectancy: parseDecimalNumber(
          service.claimant_life_expectancy,
        ),
        msa_period:
          "msa_period" in service ? parseNumber(service.msa_period) : null,
      },
      { keepDirtyValues: true },
    );
  }, [service, reset, computedMedianRatedAge, computedActiveRatedAge]);

  const handleSave = useCallback(
    (field: string) => async (value: unknown) => {
      // convert empty string to null for numeric fields
      const processedValue = value === "" ? null : value;

      // validate the value
      const validationError = validateAgeField(field, processedValue);
      if (validationError) {
        KaboodleToaster.error(validationError);
        return;
      }

      // validation passed, save the value
      onSave(field, processedValue);
    },
    [onSave],
  );

  const handleAddRatedAge = useCallback(async () => {
    if (!hasRatedAgeFields(service)) return;

    try {
      await createRatedAge({
        data: {
          msa: msaId || null,
          cost_projection: costProjectionId || null,
          life_company: "-",
          date_received: new Date().toISOString().split("T")[0],
          rated_age: null,
          organization: service.organization,
        },
      }).unwrap();
    } catch (error) {
      KaboodleToaster.error("Failed to add rated age");
    }
  }, [createRatedAge, service, msaId, costProjectionId]);

  const handleDeleteRatedAge = useCallback(
    async (ratedAge: RatedAgeType) => {
      try {
        await deleteRatedAge({
          id: ratedAge.id,
        }).unwrap();
      } catch (error) {
        KaboodleToaster.error("Failed to delete rated age");
      }
    },
    [deleteRatedAge],
  );

  const RowComponent = useCallback((ratedAge: RatedAgeType) => {
    return <RatedAgeRow {...ratedAge} />;
  }, []);

  if (!hasRatedAgeFields(service)) {
    return null;
  }

  return (
    <div className="swim-column">
      <RatedAgeOCRStatus
        msaId={msaId?.toString()}
        costProjectionId={costProjectionId?.toString()}
      />

      <FutureMedicalCard
        cardNumber={3}
        heading="Rated Ages and Life Expectancy"
      >
        <ControlledInput
          control={control}
          name="claimant_chronological_age"
          label="Chronological Age"
          errors={{}}
          onSave={handleSave("claimant_chronological_age")}
          componentType={MaskedInput}
          mask="age"
        />

        <ControlledInput
          control={control}
          name="median_rated_age"
          label="Median Rated Age"
          errors={{}}
          componentType={MaskedInput}
          mask="age"
          readOnly={true}
        />

        <ControlledInput
          control={control}
          name="rated_age_override"
          label="Rated Age Override"
          errors={{}}
          onSave={handleSave("rated_age_override")}
          componentType={MaskedInput}
          mask="age"
        />

        <ControlledInput
          control={control}
          name="active_rated_age"
          label="Active Rated Age"
          errors={{}}
          componentType={MaskedInput}
          mask="age"
          readOnly={true}
        />

        <ControlledInput
          control={control}
          name="claimant_life_expectancy"
          label="Life Expectancy"
          errors={{}}
          onSave={handleSave("claimant_life_expectancy")}
          componentType={MaskedInput}
          mask="age-decimal"
        />

        {serviceType === SERVICE_TYPE.MSA_111 && (
          <ControlledInput
            control={control}
            name="msa_period"
            label="Period"
            errors={{}}
            onSave={handleSave("msa_period")}
            componentType={MaskedInput}
            mask="age"
          />
        )}
      </FutureMedicalCard>

      <MultiColumnCard
        id="rated-ages-list"
        classNames="rated-ages-grid"
        heading="Rated Age Entries"
        columnNames={[
          "Life Company",
          "Rated Age",
          "Date Received",
          "Unrated Life Expectancy",
        ]}
        initialRows={ratedAges}
        RowComponent={RowComponent}
        onNewRow={handleAddRatedAge}
        onDeleteRow={handleDeleteRatedAge}
        getRowKey={(row) => row.id}
        isLoading={isLoadingRatedAges}
        leftAlignColumns={["Life Company"]}
      />
    </div>
  );
};

export default RatedAgeForm;
